import React, { Suspense, useMemo, useState } from 'react';
import FloatingButton from '../../../../components/ui/materialui/floatingButton';
import MButton from '../../../../components/inputs/yup/materialui/mButton';
import { Box, Grid2, Paper } from '@mui/material';
import Breadcrumb from '../../../../components/ui/materialui/breadcrumb';
import Tabla from '../../../../components/ui/materialui/serverSideTable';
import { useDataFetch } from '../../../../hooks/useDataFetch2';
import TableHeader from '../../../../components/ui/materialui/tableHeader';
import { exportExcel } from '../../../../utils/exportExcel';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import SummarizeTwoToneIcon from '@mui/icons-material/SummarizeTwoTone';
import { useColumns } from '../utils/columns';
import { TasaEditar } from '../models/tasa';
import ModalTasa from '../components/modalTasa';
import CustomAccordion from '../../../../components/ui/materialui/customAccordion';
import InputSelect from '../../../../components/inputs/yup/materialui/inputSelect';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { EnumTipoEstadoRegistro, EstadoRegistro, TipoFuncionalidad } from '../../../../utils/enums';
import InputFecha from '../../../../components/inputs/yup/materialui/inputFecha';
import { FilterList } from '@mui/icons-material';
import * as yup from 'yup';
import { PermissionGuard } from '../../../../components/auth/ProtectedRoute';

interface FiltroTasas {
  estado: string;
  fechaDesde?: Date | null;
  fechaHasta?: Date | null;
}



const validationSchema = yup.object({
  estado: yup.string().required("Campo requerido"),
  fechaDesde: yup
    .date()
    .nullable()
    .test("ambas-fechas", "Debe ingresar ambas fechas o ninguna", function (value) {
      const { fechaHasta } = this.parent;
      const soloUna = (value && !fechaHasta) || (!value && fechaHasta);
      return !soloUna;
    })
    .test("fecha-desde-menor", "La fecha desde no puede ser mayor que la fecha hasta", function (value) {
      const { fechaHasta } = this.parent;
      if (value && fechaHasta) {
        return value <= fechaHasta;
      }
      return true; 
    }),
  fechaHasta: yup.date().nullable(),
});


function Tasa() {

  const [modalDetalle, setModalDetalle] = useState<boolean>(false)
  const [tasasEditar, setTasasEditar] = useState<TasaEditar>();

  const urlTabla = `siscred-api-maestros/v1/tasa/listar`;

  const controlTabla = useDataFetch(
    urlTabla,
    new URLSearchParams('filters.status=ACTIVO'),
    true,
  )
  const {
    formState: { errors },
    control,
    handleSubmit,
  } = useForm<FiltroTasas>({
    resolver: yupResolver(validationSchema),
    mode: 'onBlur'
  })

  const { COLUMNS } = useColumns({ controlTabla, setTasasEditar, setModalDetalle });
  //eslint-disable-next-line
  const columns = useMemo(() => COLUMNS, [controlTabla]);
  const breadcrumbItems = [
    { label: 'Inicio', url: '/' },
    { label: 'Maestros', url: `/maestros` },
    { label: 'Tasas', active: true },
  ]


  const onSubmit = async (data: FiltroTasas) => {    

    controlTabla.manejarCambiosFiltros(
      'filters.fechaDesde',
      '',
    )

    controlTabla.manejarCambiosFiltros(
      'filters.fechaHasta',
      '',
    )

    if (data.estado) {
      controlTabla.manejarCambiosFiltros(
        'filters.status',
        data.estado.toString(),
      );
    }

    if (data.fechaDesde) {
      controlTabla.manejarCambiosFiltros(
        'filters.fechaDesde',
        data.fechaDesde.toISOString(),
      )
    }

    if (data.fechaHasta) {
      controlTabla.manejarCambiosFiltros(
        'filters.fechaHasta',
        data.fechaHasta.toISOString(),
      )
    }

    await controlTabla.fetchData();
  }

  return (
    <>
      <Suspense>
        <Breadcrumb items={breadcrumbItems} />
        <Box sx={{ mt: 2 }}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Suspense>
              <CustomAccordion
                title='Filtros'
                expandedDefault
              >
                <form onSubmit={handleSubmit(onSubmit)}>
                  <Grid2 container spacing={2}>
                    <Grid2 size={3}>
                      <InputSelect
                        name="estado"
                        control={control}
                        error={errors.estado}
                        label="Estado"
                        options={[
                          { value: EstadoRegistro.Todas, label: EstadoRegistro.Todas },
                          { value: EstadoRegistro.Activo, label: EstadoRegistro.Activo },
                          { value: EstadoRegistro.Inactivo, label: EstadoRegistro.Inactivo },
                        ]}
                      />
                    </Grid2>
                    <Grid2 size={3}>
                      <InputFecha
                        name="fechaDesde"
                        control={control}
                        label="Fecha Desde"
                        error={errors.fechaDesde}
                      />
                    </Grid2>
                    <Grid2 size={3}>
                      <InputFecha
                        name="fechaHasta"
                        control={control}
                        label="Fecha Hasta"
                        error={errors.fechaHasta}
                      />
                    </Grid2>
                    <Grid2 size={3} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                      <MButton type="submit" variant="contained" color="primary" startIcon={<FilterList />}>
                        Filtrar
                      </MButton>
                    </Grid2>
                  </Grid2>
                </form>
              </CustomAccordion>
              <Tabla
                columns={columns}
                control={controlTabla}
                url={urlTabla}
                titulo={
                  <TableHeader
                    title=""
                    control={controlTabla}
                    button={
                      <PermissionGuard permissions={[TipoFuncionalidad.Maestros_editar]}>
                      <MButton
                        type="button"
                        variant="contained"
                        onClick={() => { setModalDetalle(true); setTasasEditar(undefined); }}
                        startIcon={<AddTwoToneIcon />}
                      >
                        Nuevo
                      </MButton>
                      </PermissionGuard>
                    }
                    buttonExport={
                      <PermissionGuard permissions={[TipoFuncionalidad.Transversal_exportar]}>
                      <MButton
                        type="button"
                        variant="outlined"
                        onClick={() => exportExcel(controlTabla.data, columns, "TASA")}
                        startIcon={<SummarizeTwoToneIcon />}
                      >
                        Exportar
                      </MButton>
                      </PermissionGuard>
                    }
                  />
                }
              />
            </Suspense>
          </Paper>
        </Box>


      </Suspense>
      <ModalTasa
        show={modalDetalle}
        datosEditar={tasasEditar}
        onHide={() => setModalDetalle(false)}
        refresh={() => controlTabla.fetchData()}
      />

    </>
  );
}

export default Tasa;
